import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useLoginMutation } from "@/api/authApi";
import { useAppSelector } from "@/store/hooks";
import { useEffect, useState } from "react";
import Button from "@mui/material/Button";

import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { Stack, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { type FormType, schema } from "@/schemas/login";
import { BrowserCompatibilityAlert } from "@/support/BrowserCompatibilityAlert.tsx";
import { ROLE_ADMIN, ROLE_SUPER_ADMIN } from "@/constants";
import { useTranslation } from "react-i18next";

type LoginSearch = {
  redirect?: string;
};

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    return {
      redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    };
  },
});

const defaultValues = {
  email: "",
  password: "",
};

function LoginPage() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const navigate = useNavigate();
  const { redirect: redirectUrl } = Route.useSearch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const userRole = useAppSelector((state) => state.auth.user?.role);
  const [login, { isLoading, error }] = useLoginMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
    resolver: zodResolver(schema),
  });

  // Redirect if already authenticated.
  // Uses navigate() (SPA navigation) instead of window.location.href to:
  // 1. Avoid a full page reload that causes a race condition with redux-persist rehydration
  // 2. Prevent open redirect vulnerabilities (navigate only handles internal routes)
  useEffect(() => {
    if (isAuthenticated && userRole) {
      const isAdmin = userRole === ROLE_ADMIN || userRole === ROLE_SUPER_ADMIN;
      const destination = redirectUrl ?? (isAdmin ? "/admin" : "/");
      navigate({ to: destination });
    }
  }, [isAuthenticated, userRole, navigate, redirectUrl]);

  const onSubmit = async (data: FormType) => {
    const { email, password } = data;

    const res = await login({
      email: email.trim(),
      password: password.trim(),
    });
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={(theme) => ({
          backgroundColor: theme.palette.background.default,
          height: "100vh",
        })}
      >
      <Box
        sx={(theme) => ({
          width: "100%",
          maxWidth: 500,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          p: 4,
        })}
      >
        <Stack gap={2} sx={{ my: 6 }}>
          <BrowserCompatibilityAlert />
          <Typography variant="h3" sx={{ mb: 1.5 }}>
            {t("login.welcome")}
          </Typography>
        </Stack>
        {}
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2} sx={{ mb: 4 }}>
            <Controller
              name="email"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  fullWidth
                  autoFocus
                  label={t("login.email")}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  error={Boolean(errors.email)}
                  InputProps={{
                    size: "medium",
                  }}
                  inputProps={{
                    "data-testid": "login-email-input",
                  }}
                  {...(errors.email && {
                    helperText: errors.email.message,
                  })}
                />
              )}
            />
            <Box sx={{ mb: 1.5 }}>
              <Controller
                name="password"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange, onBlur } }) => (
                  <TextField
                    fullWidth
                    value={value}
                    onBlur={onBlur}
                    label={t("login.password")}
                    onChange={onChange}
                    id="auth-login-v2-password"
                    error={Boolean(errors.password)}
                    {...(errors.password && {
                      helperText: errors.password.message,
                    })}
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                      size: "medium",
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setShowPassword(!showPassword)}
                            data-testid={"show-password-button"}
                          >
                            {showPassword ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      "data-testid": "login-password-input",
                    }}
                  />
                )}
              />
            </Box>

            <Button fullWidth type="submit" variant="contained" sx={{ mb: 4 }}>
              {t("login.submit")}
            </Button>
          </Stack>
        </form>
      </Box>
    </Stack>
    </>
  );
}
